import {startTransition, useCallback, useEffect, useMemo, useState, type ReactNode} from 'react';
import {Box, Button, Skeleton, Stack, Typography} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import {SplitTabsPanel} from '@/components/common';
import {
    AuditProgramSectionVO,
    getWSectionVO,
    saveWSectionPage,
} from '@/services/auditService/auditProgramService';
import {
    W3_1_PAGE,
    W6_PAGE,
    WS_PAGE,
    W_PAGE_TITLES,
    W_SECTION_INDEX,
    W_SECTION_INDEX_LETTER,
    W_SECTION_INDEX_TITLE,
    W_SECTION_PAGES,
    getWProgrammePage,
    type W3_1Page,
    type W6Page,
    type WProgrammePage,
    type WSectionPageCode,
    type WSummaryPage,
} from '@/pages/components/audit-program/w-section/constants';
import W3_1ComponentsView from '@/pages/components/audit-program/w-section/W3_1ComponentsView';
import W6ReportView from '@/pages/components/audit-program/w-section/W6ReportView';
import EsSummaryView from '@/pages/components/audit-program/e-section/EsSummaryView';
import G0ProgrammeView from '@/pages/components/audit-program/g-section/G0ProgrammeView';
import PageTabLabel from '@/pages/components/audit-program/PageTabLabel';
import SectionIndexView from '@/pages/components/audit-program/SectionIndexView';
import {useProgramSectionTemplates} from '@/pages/components/audit-program/templates/useProgramSectionTemplates';

export type WSectionViewProps = {
    companyId: number;
    annualId: number;
    companyName?: string | null;
    initialPage?: WSectionPageCode;
};

const DISPLAY_ONLY_PAGES = new Set<WSectionPageCode>(['Wi']);

function toStringRecord(data?: Record<string, unknown>): Record<string, string> {
    if (!data) return {};
    return Object.fromEntries(
        Object.entries(data).map(([k, v]) => [k, v == null ? '' : String(v)]),
    );
}

function withHeaderDefaults(
    page: Record<string, string>,
    data: AuditProgramSectionVO,
    refCode: string,
): Record<string, string> {
    return {
        ...page,
        client: page.client || data.client || '',
        yearEnd: page.yearEnd || data.yearEnd || '',
        fileNo: page.fileNo || data.fileNo || '',
        preparedBy: page.preparedBy || data.preparedInitial || data.preparedBy || '',
        completedBy: page.completedBy || data.preparedInitial || data.preparedBy || '',
        reviewedBy: page.reviewedBy || data.reviewedInitial || data.reviewedBy || '',
        ref: page.ref || refCode,
    };
}

export default function WSectionView({
    companyId,
    annualId,
    companyName,
    initialPage = 'Wi',
}: WSectionViewProps) {
    const [pageKey, setPageKey] = useState<WSectionPageCode>(initialPage);
    const [header, setHeader] = useState<AuditProgramSectionVO | null>(null);
    const [pageData, setPageData] = useState<Record<string, Record<string, string>>>({});
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [dirty, setDirty] = useState<Record<string, boolean>>({});
    const {getContent} = useProgramSectionTemplates('W');

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await getWSectionVO(companyId, annualId);
            if (companyName && !data.client) {
                data.client = companyName;
            }
            setHeader(data);
            const pages: Record<string, Record<string, string>> = {};
            for (const code of W_SECTION_PAGES) {
                if (code === 'Wi') {
                    pages[code] = toStringRecord(data.pages?.[code]);
                    continue;
                }
                pages[code] = withHeaderDefaults(toStringRecord(data.pages?.[code]), data, code);
            }
            setPageData(pages);
            setDirty({});
        } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : 'Failed to load W Section';
            setError(msg);
            setHeader(null);
        } finally {
            setLoading(false);
        }
    }, [companyId, annualId, companyName]);

    useEffect(() => {
        void load();
    }, [load]);

    useEffect(() => {
        setPageKey(initialPage);
    }, [initialPage]);

    const handleFieldChange = useCallback((pageCode: WSectionPageCode, key: string, value: string) => {
        setPageData((prev) => ({
            ...prev,
            [pageCode]: {...(prev[pageCode] ?? {}), [key]: value},
        }));
        setDirty((prev) => ({...prev, [pageCode]: true}));
    }, []);

    const handleSave = useCallback(async () => {
        if (DISPLAY_ONLY_PAGES.has(pageKey) || !dirty[pageKey]) return;
        setSaving(true);
        setError(null);
        try {
            await saveWSectionPage(companyId, annualId, pageKey, pageData[pageKey] ?? {});
            setDirty((prev) => ({...prev, [pageKey]: false}));
        } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : 'Failed to save';
            setError(msg);
        } finally {
            setSaving(false);
        }
    }, [companyId, annualId, dirty, pageKey, pageData]);

    const indexRows = useMemo(() => {
        const fromApi = getContent('Wi') as {rows?: typeof W_SECTION_INDEX} | undefined;
        return fromApi?.rows?.length ? fromApi.rows : W_SECTION_INDEX;
    }, [getContent]);

    const tabs = useMemo(() => W_SECTION_PAGES.map((code) => {
        const programmeFallback = getWProgrammePage(code);
        let content: ReactNode = null;
        if (header) {
            if (code === 'Wi') {
                content = (
                    <SectionIndexView
                        header={header}
                        refCode="Wi"
                        sectionLetter={W_SECTION_INDEX_LETTER}
                        sectionTitle={W_SECTION_INDEX_TITLE}
                        rows={indexRows}
                        onNavigate={(pageCode) => startTransition(() => setPageKey(pageCode as WSectionPageCode))}
                    />
                );
            } else if (code === 'Ws') {
                content = (
                    <EsSummaryView
                        header={header}
                        page={(getContent('Ws') ?? WS_PAGE) as WSummaryPage}
                        fallback={WS_PAGE}
                        refCode="Ws"
                        values={pageData.Ws ?? {}}
                        onChange={(k, v) => handleFieldChange('Ws', k, v)}
                    />
                );
            } else if (code === 'W3.1') {
                content = (
                    <W3_1ComponentsView
                        header={header}
                        page={(getContent('W3.1') ?? W3_1_PAGE) as W3_1Page}
                        refCode="W3.1"
                        values={pageData['W3.1'] ?? {}}
                        onChange={(k, v) => handleFieldChange('W3.1', k, v)}
                    />
                );
            } else if (code === 'W6') {
                content = (
                    <W6ReportView
                        header={header}
                        page={(getContent('W6') ?? W6_PAGE) as W6Page}
                        refCode="W6"
                        values={pageData.W6 ?? {}}
                        onChange={(k, v) => handleFieldChange('W6', k, v)}
                    />
                );
            } else if (programmeFallback) {
                content = (
                    <G0ProgrammeView
                        header={header}
                        page={(getContent(code) ?? programmeFallback) as WProgrammePage}
                        refCode={code}
                        values={pageData[code] ?? {}}
                        onChange={(k, v) => handleFieldChange(code, k, v)}
                    />
                );
            }
        }
        return {
            key: code,
            label: <PageTabLabel code={code} title={W_PAGE_TITLES[code]}/>,
            icon: code,
            content,
        };
    }), [header, pageData, handleFieldChange, indexRows, getContent]);

    if (loading) {
        return <Skeleton variant="rectangular" height={480} sx={{m: 2}}/>;
    }

    if (error && !header) {
        return (
            <Box sx={{p: 2}}>
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }

    const saveDisabled = DISPLAY_ONLY_PAGES.has(pageKey) || !dirty[pageKey] || saving;

    return (
        <Box sx={{height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column'}}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{px: 2, pt: 1, pb: 0.5}}>
                <Typography variant="subtitle1" fontWeight={600}>
                    W Section — Consolidation
                </Typography>
                <Button
                    size="small"
                    variant="contained"
                    startIcon={<SaveIcon/>}
                    disabled={saveDisabled}
                    onClick={() => void handleSave()}
                >
                    {saving ? 'Saving…' : 'Save'}
                </Button>
            </Stack>
            {error ? (
                <Typography color="error" variant="body2" sx={{px: 2}}>{error}</Typography>
            ) : null}
            <Box sx={{flex: 1, minHeight: 0}}>
                <SplitTabsPanel
                    tabs={tabs}
                    selectedKey={pageKey}
                    onChange={(k) => setPageKey(k as WSectionPageCode)}
                    height="100%"
                    allowMaximize={false}
                    contentWrapperSx={{height: '100%', overflow: 'auto', p: 0}}
                    rightPaneSx={{height: '100%', overflow: 'hidden'}}
                />
            </Box>
        </Box>
    );
}
